import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CarouselItem, MAX_CAROUSEL_ITEMS } from '@/types/carousel.types'
import { fetchCarouselConfig, saveCarouselConfig } from './carouselThunks'

export interface CarouselState {
  /** Ítems dinámicos en orden. El ítem fijo se agrega en runtime por `selectCarouselItemsWithFixed`. */
  items: CarouselItem[]
  /**
   * Snapshot de `items` al momento de abrir el modal. Permite (a) revertir cambios al
   * cerrar sin guardar y (b) computar `selectIsCarouselDirty` para habilitar el botón
   * "Guardar". No se expone fuera del slice — su rol es de bookkeeping interno.
   */
  pristineItems: CarouselItem[]
  isLoading: boolean
  isSaving: boolean
  error: string | null
  lastUpdatedAt: string | null
  isEditModalOpen: boolean
}

const initialState: CarouselState = {
  items: [],
  pristineItems: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastUpdatedAt: null,
  isEditModalOpen: false,
}

const carouselSlice = createSlice({
  name: 'carousel',
  initialState,
  reducers: {
    setCarouselItems: (state, action: PayloadAction<CarouselItem[]>) => {
      state.items = action.payload.map((item, i) => ({ ...item, order: i }))
    },
    addCarouselItem: (state, action: PayloadAction<CarouselItem>) => {
      if (state.items.length >= MAX_CAROUSEL_ITEMS) return
      state.items.push({
        fitMode: 'cover',
        ...action.payload,
        order: state.items.length,
      })
    },
    setItemFitMode: (
      state,
      action: PayloadAction<{ index: number; fitMode: 'cover' | 'contain' }>
    ) => {
      const { index, fitMode } = action.payload
      if (index < 0 || index >= state.items.length) return
      state.items[index] = { ...state.items[index], fitMode }
    },
    removeCarouselItem: (state, action: PayloadAction<number>) => {
      const index = action.payload
      if (index < 0 || index >= state.items.length) return
      state.items.splice(index, 1)
      state.items.forEach((item, i) => {
        item.order = i
      })
    },
    moveItemUp: (state, action: PayloadAction<number>) => {
      const index = action.payload
      if (index <= 0 || index >= state.items.length) return
      const tmp = state.items[index - 1]
      state.items[index - 1] = { ...state.items[index], order: index - 1 }
      state.items[index] = { ...tmp, order: index }
    },
    moveItemDown: (state, action: PayloadAction<number>) => {
      const index = action.payload
      if (index < 0 || index >= state.items.length - 1) return
      const tmp = state.items[index + 1]
      state.items[index + 1] = { ...state.items[index], order: index + 1 }
      state.items[index] = { ...tmp, order: index }
    },
    openEditModal: (state) => {
      state.isEditModalOpen = true
      state.pristineItems = state.items.map((item) => ({ ...item }))
    },
    closeEditModal: (state) => {
      state.items = state.pristineItems.map((item) => ({ ...item }))
      state.isEditModalOpen = false
    },
    clearCarouselError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarouselConfig.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCarouselConfig.fulfilled, (state, action: PayloadAction<CarouselItem[]>) => {
        state.isLoading = false
        const normalized = action.payload.map((item, i) => ({ ...item, order: i }))
        state.items = normalized
        state.pristineItems = normalized.map((item) => ({ ...item }))
        state.error = null
      })
      .addCase(fetchCarouselConfig.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || action.error?.message || 'Error al cargar el carrusel.'
      })
      .addCase(saveCarouselConfig.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(saveCarouselConfig.fulfilled, (state, action) => {
        state.isSaving = false
        state.items = action.payload.items.map((item) => ({ ...item }))
        state.pristineItems = action.payload.items.map((item) => ({ ...item }))
        state.lastUpdatedAt = action.payload.updatedAt
        state.isEditModalOpen = false
        state.error = null
      })
      .addCase(saveCarouselConfig.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.payload || action.error?.message || 'Error al guardar el carrusel.'
      })
  },
})

export const {
  setCarouselItems,
  addCarouselItem,
  setItemFitMode,
  removeCarouselItem,
  moveItemUp,
  moveItemDown,
  openEditModal,
  closeEditModal,
  clearCarouselError,
} = carouselSlice.actions

export default carouselSlice.reducer
